import { type Span, trace } from "@opentelemetry/api";
import cuid from "cuid";

import { createLogger } from "@/logger";

const log = createLogger("telemetry");

import type React from "react";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import LoadingScreen from "@/components/LoadingScreen";
import { config } from "@/config.ts";
import { AuthContext } from "@/models/StateProviders/AuthProvider.tsx";
import { PerformanceMetricsService } from "@/services/PerformanceMetricsService";

export interface ActiveSpanInfo {
  action: string;
  target?: string;
  startTime: number;
  span: Span;
}

interface TelemetryContextProps {
  setAttribute: (key: string, value: string) => void;
  generateSpan: (props: { traceName?: string; spanName: string }) => Span;
  observeActionToPaint: (props: { action: string; target?: string }) => Span;
  trackAction: (props: { action: string; target?: string }, customAttributes?: Record<string, any>) => Span;
  finishAction: (props: { action: string; target?: string }, customAttributes?: Record<string, any>) => void;
  getActiveSpans: () => Map<string, ActiveSpanInfo>;
  subscribeToSpanUpdates: (callback: () => void) => () => void;
}

export const TelemetryContext = createContext<TelemetryContextProps | undefined>(undefined);

const attributes = new Map<string, string>();
const activeSpans = new Map<string, ActiveSpanInfo>();
const spanUpdateListeners = new Set<() => void>();

const notifySpanUpdate = () => {
  spanUpdateListeners.forEach((listener) => listener());
};

attributes.set("buildVersion", config.buildVersion);
attributes.set("buildSha", config.buildSha);
attributes.set("buildRef", config.buildRef);
attributes.set("buildTags", config.buildTags?.join(","));
attributes.set("buildAuthor", config.buildAuthor);
attributes.set("buildMessage", config.buildMessage);
attributes.set("buildDate", config.buildDate);
attributes.set("webRc", config.webRc);
attributes.set("webRelease", config.webRelease);

interface ItemTelemetry {
  feedId: string;
  id: string;
}
interface FeedTelemetry {
  id: string;
}

function targetForItem(item: ItemTelemetry) {
  return `feed#${item.feedId}\nitem#${item.id}`;
}
function targetForFeed(feed: FeedTelemetry) {
  return `feed#${feed.id}`;
}

export const actions = {
  feedNavigate: (feedId: string) => ({
    action: "feed.navigate",
    target: `feed#${feedId}`,
  }),

  feedItemTextLoading: (item: ItemTelemetry) => ({
    action: "feed.item.text.loading",
    target: targetForItem(item),
  }),

  feedItemAudioLoading: (item: ItemTelemetry) => ({
    action: "feed.item.audio.loading",
    target: targetForItem(item),
  }),

  feedItemTextTts: (item: ItemTelemetry) => ({
    action: "feed.item.text.tts",
    target: targetForItem(item),
  }),

  feedItemAudioTts: (item: ItemTelemetry) => ({
    action: "feed.item.audio.tts",
    target: targetForItem(item),
  }),

  feedInitial: (feed: FeedTelemetry) => ({
    action: "feed.initial",
    target: targetForFeed(feed),
  }),

  feedScroll: (feed: FeedTelemetry) => ({
    action: "feed.scroll",
    target: targetForFeed(feed),
  }),

  bootstrapLoad: () => ({ action: "bootstrap.load" }),

  bootstrapFull: () => ({ action: "bootstrap.full" }),

  bootstrapNetwork: (name: string, page: number) => ({
    action: "bootstrap.network",
    target: `${name}\npage ${page}`,
  }),

  bootstrapDbWrite: (name: string, page: number) => ({
    action: "bootstrap.dbwrite",
    target: `${name}\npage ${page}`,
  }),

  bootstrapAfterHooksInitialUnreadItems: () => ({
    action: "bootstrap.afterHooks.initialUnreadItems",
  }),

  warmupCalculate: () => ({
    action: "warmup.calculate",
  }),

  warmupFetchItems: (feedId?: string, feedName?: string) => ({
    action: "warmup.fetch_items",
    target: feedId ? `feed#${feedId}${feedName ? `\n${feedName}` : ""}` : undefined,
  }),

  warmupFetchContent: () => ({
    action: "warmup.fetch_content",
  }),

  warmupFull: () => ({
    action: "warmup.full",
  }),
};

export const TelemetryProvider: React.FC<{ children: ReactNode; active?: boolean }> = ({ children, active = true }) => {
  const { loggedIn } = useContext(AuthContext);
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    if (!active) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        for (const spanInfo of activeSpans.values()) {
          spanInfo.span.setAttribute("tabLostFocus", true);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [active]);

  const setAttribute = (key: string, value: string) => {
    attributes.set(key, value);
  };

  const trackAction = (
    { action, target }: { action: string; target?: string },
    customAttributes?: Record<string, any>,
  ) => {
    if (!active) {
      return trace.getTracer("noop-tracer").startSpan("noop");
    }
    const spanName = `${action}#${target}`;
    if (activeSpans.has(spanName)) {
      return activeSpans.get(spanName).span;
    }
    const tracer = trace.getTracer("action-tracer");
    const span = tracer.startSpan(`Action ${action}`);
    log.info("Action Start: ", {
      action,
      target,
    });
    span.setAttribute("action", action);
    if (target) span.setAttribute("target", target);
    if (customAttributes) {
      for (const [key, value] of Object.entries(customAttributes)) {
        span.setAttribute(key, value);
      }
    }
    activeSpans.set(spanName, {
      action,
      target,
      startTime: Date.now(),
      span,
    });
    notifySpanUpdate();
    return span;
  };

  const finishAction = (
    { action, target }: { action: string; target?: string },
    customAttributes: Record<string, any> = {},
  ) => {
    if (!active) return;
    const spanName = `${action}#${target}`;
    if (activeSpans.has(spanName)) {
      const spanInfo = activeSpans.get(spanName);
      const span = spanInfo.span;
      const endTime = Date.now();
      const duration = endTime - spanInfo.startTime;

      log.info("Action Finish: ", {
        action,
        target,
        attributes,
        duration,
      });

      for (const [key, value] of attributes.entries()) {
        span.setAttribute(key, value);
      }
      for (const [key, value] of Object.entries(customAttributes)) {
        span.setAttribute(key, value);
      }
      span.end();
      activeSpans.delete(spanName);
      notifySpanUpdate();

      PerformanceMetricsService.recordMetric({
        id: cuid(),
        action,
        target,
        startTime: spanInfo.startTime,
        endTime,
        duration,
        customAttributes,
      }).catch((err) => {
        log.warn("Failed to record performance metric:", err);
      });
    }
  };

  useEffect(() => {
    const workspaceId = params.workspaceId;
    const feedId = params.feedId;
    setAttribute("workspaceId", workspaceId);
    setAttribute("feedId", feedId);
  }, [params]);

  const generateSpan = ({ traceName, spanName }: { traceName?: string; spanName: string }) => {
    if (!active) {
      return trace.getTracer("noop-tracer").startSpan("noop");
    }
    const tracer = trace.getTracer(traceName || "default-tracer");
    const span = tracer.startSpan(spanName);
    for (const [key, value] of attributes.entries()) {
      span.setAttribute(key, value);
    }
    return span;
  };

  const observeActionToPaint = ({ action, target }: { action: string; target?: string }) => {
    if (!active) {
      return trace.getTracer("noop-tracer").startSpan("noop");
    }
    const span = generateSpan({
      spanName: `Action ${action}`,
      traceName: "action-tracer",
    });
    const startTime = Date.now();
    span.setAttribute("action", action);
    if (target) span.setAttribute("target", target);
    log.info("Action Start: ", {
      action,
      target,
      startTime,
    });

    window.requestAnimationFrame(() => {
      const time = Date.now() - startTime;
      span.setAttribute("next-paint-time", time);
      span.end();
      log.info("Action: ", {
        action,
        target,
        time,
      });
    });

    return span;
  };

  useEffect(() => {
    if (!active) return;
    const span = observeActionToPaint({
      action: "navigate",
      target: location.pathname,
    });
    span.setAttribute("pathname", location.pathname);
    return () => {
      if (!span.isRecording()) return;
      span.end();
    };
  }, [location, active]);

  useEffect(() => {
    if (!active) return;
    const handleClick = (event) => {
      const target = `${event.target.tagName} ${event.target.textContent}`;
      observeActionToPaint({ action: "click", target });
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [active]);

  const getActiveSpans = () => {
    return new Map(activeSpans);
  };

  const subscribeToSpanUpdates = (callback: () => void) => {
    spanUpdateListeners.add(callback);
    return () => {
      spanUpdateListeners.delete(callback);
    };
  };

  return (
    <TelemetryContext.Provider
      value={{
        setAttribute,
        generateSpan,
        observeActionToPaint,
        trackAction,
        finishAction,
        getActiveSpans,
        subscribeToSpanUpdates,
      }}
    >
      {loggedIn ? <>{children}</> : <LoadingScreen />}
    </TelemetryContext.Provider>
  );
};

export const useTelemetry = (): TelemetryContextProps => {
  const context = useContext(TelemetryContext);
  if (!context) {
    throw new Error("useTelemetry must be used within a TelemetryProvider");
  }
  return context;
};
